/**
 * V212.1: Native Android OAuth Authentication Helpers
 * V260: Rewrote Cloudinary upload with detailed step-by-step logging
 * 
 * Provides platform-aware OAuth redirect URL handling for native Android deep linking
 */

import { Capacitor } from '@capacitor/core';
// V333.3: Use same client as useAuth to prevent auth loop (session stored/read by single instance)
import { supabase } from '../integrations/supabase/client';

/**
 * Returns the appropriate OAuth redirect URL based on platform
 * - Native iOS + Android: Custom scheme (com.blissnet.app://auth-callback) so Google
 *   redirects into appUrlOpen PKCE completion — never loads https://blissnet.app in WebView.
 * - Web: HTTPS callback on current origin (cache-bust query preserved).
 *
 * HOTFIX V2: Restored iOS custom scheme; Mar 2026 HTTPS-only iOS redirect left no Universal
 * Link handler and stranded users on production web. Scheme is registered in iOS Info.plist
 * (CFBundleURLSchemes com.blissnet.app). Supabase + Google OAuth must allowlist the same URI.
 */
export function getOAuthRedirectTo(): string {
  const platform = Capacitor.getPlatform();
  const DEV = import.meta.env.DEV;

  if (platform === 'ios' || platform === 'android') {
    const redirectTo = 'com.blissnet.app://auth-callback';
    if (DEV) {
      console.debug(`[AUTHFLOW] redirectTo selected platform=${platform} value=${redirectTo}`);
    }
    return redirectTo;
  }

  const buildVersion = import.meta.env.VITE_APP_VERSION || Date.now();
  const redirectTo = `${window.location.origin}/auth/callback?v=${buildVersion}`;
  if (DEV) {
    console.debug(`[AUTHFLOW] redirectTo selected platform=${platform} value=${redirectTo}`);
  }
  return redirectTo;
}

/**
 * Exchanges OAuth code for session (PKCE flow)
 * This must be called when the deep link is received
 * 
 * @param code - The authorization code from the OAuth callback
 * @returns Promise with session data or error
 */
export async function exchangeCodeForSession(code: string) {
  const DEV = import.meta.env.DEV;
  try {
    if (DEV) {
      console.debug('[AUTH] exchange start');
    }
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    
    if (error) {
      console.error(`[AUTH] exchange success=false error=${error.message || 'unknown'}`);
      console.error('❌ V212.1: Failed to exchange code for session:', error);
      return { data: null, error };
    }
    
    if (DEV) {
      console.debug('[AUTH] exchange success=true error=');
      console.debug('✅ V212.1: Successfully exchanged code for session');
    }
    return { data, error: null };
  } catch (err: any) {
    console.error(`[AUTH] exchange success=false error=${err?.message || 'unknown'}`);
    console.error('❌ V212.1: Exception during code exchange:', err);
    return { data: null, error: err };
  }
}

/**
 * V212.3: Ensure user profile exists in database after OAuth
 * 
 * This handles Android deep link auth where profile creation might not
 * happen automatically due to metadata timing issues.
 * 
 * Creates both 'users' and 'profiles' table records with OAuth metadata.
 * 
 * @param userId - The authenticated user's ID
 * @param session - The Supabase session object
 * @returns Promise<boolean> - true if profile created/updated successfully
 */
export async function ensureUserProfile(userId: string, session: any): Promise<boolean> {
  console.log('[V212 Profile] ========================================');
  console.log('[V212 Profile] Starting profile creation for:', userId);
  console.log('[V212 Profile] Platform:', Capacitor.getPlatform());
  console.log('[V212 Profile] Timestamp:', new Date().toISOString());
  
  try {
    const user = session.user;
    if (!user) {
      console.error('[V212 Profile] ❌ No user object in session');
      return false;
    }
    
    // Extract metadata from multiple possible locations
    const metadata = user.user_metadata || {};
    const identityData = user.identities?.[0]?.identity_data || {};
    
    // Extract name from multiple possible sources
    const fullName = 
      metadata.full_name || 
      metadata.name || 
      identityData.full_name || 
      identityData.name ||
      user.email?.split('@')[0] || 
      'User';
    
    // Extract avatar URL from multiple possible sources
    const avatarUrl = 
      metadata.avatar_url || 
      metadata.picture || 
      identityData.avatar_url || 
      identityData.picture ||
      null;
    
    console.log('[V212 Profile] Extracted metadata:');
    console.log('[V212 Profile] - Name:', fullName);
    console.log('[V212 Profile] - Avatar URL:', avatarUrl);
    console.log('[V212 Profile] - Email:', user.email);
    console.log('[V212 Profile] - Full metadata:', JSON.stringify(metadata, null, 2));
    console.log('[V212 Profile] - Identity data:', JSON.stringify(identityData, null, 2));
    
    // V275.8: CRITICAL - Check if user already has Cloudinary photo BEFORE uploading
    // This prevents overwriting real photos with Google avatars on every login!
    console.log('[V275.8] Checking if user already has Cloudinary photo...');

    const { data: userPhotoCheck } = await supabase
      .from('users')
      .select('photo_urls')
      .eq('id', userId)
      .maybeSingle();

    const hasCloudinaryPhoto = userPhotoCheck?.photo_urls?.some((url: string) =>
      url && url.includes('cloudinary.com')
    );

    console.log('[V275.8] User has Cloudinary photo:', hasCloudinaryPhoto);
    if (hasCloudinaryPhoto) {
      console.log('[V275.8] ✅ SKIPPING Google avatar upload - user already has Cloudinary photo');
      console.log('[V275.8] Existing photo:', userPhotoCheck.photo_urls[0]?.substring(0, 80));
    }

    // V260: Upload Google profile picture to Cloudinary with detailed logging
    // V275.8: ONLY if user doesn't already have a Cloudinary photo!
    let finalPhotoUrl: string | null = avatarUrl;
    let publicId: string | null = null;

    if (avatarUrl?.includes('googleusercontent.com') && !hasCloudinaryPhoto) {
      console.log('═══════════════════════════════════════════════════════');
      console.log('[V275.8] 📸 GOOGLE PROFILE PICTURE UPLOAD');
      console.log('═══════════════════════════════════════════════════════');
      console.log('[V275.8] Platform:', Capacitor.getPlatform());
      console.log('[V275.8] Google URL:', avatarUrl);
      console.log('[V275.8] User ID:', userId);
      console.log('[V275.8] User Name:', fullName);

      try {
        // Step 1: Fetch image from Google
        console.log('[V275.8] Step 1: Fetching image from Google...');
        
        const fetchTimeout = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Fetch timeout after 10s')), 10000)
        );
        
        const fetchResponse = await Promise.race([
          fetch(avatarUrl),
          fetchTimeout
        ]) as Response;
        
        if (!fetchResponse.ok) {
          throw new Error(`Google fetch failed: ${fetchResponse.status} ${fetchResponse.statusText}`);
        }
        
        console.log('[V260] ✅ Step 1 complete - Image fetched from Google');
        console.log('[V260] Response status:', fetchResponse.status);
        console.log('[V260] Content type:', fetchResponse.headers.get('content-type'));
        
        // Step 2: Convert to blob
        console.log('[V260] Step 2: Converting to blob...');
        const blob = await fetchResponse.blob();
        console.log('[V260] ✅ Step 2 complete - Blob created');
        console.log('[V260] Blob size:', blob.size, 'bytes');
        console.log('[V260] Blob type:', blob.type);
        
        // Step 3: Create File object from blob
        console.log('[V260] Step 3: Creating File object...');
        const fileName = `${userId}.webp`;
        const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        console.log('[V260] ✅ Step 3 complete - File object created');
        console.log('[V260] File name:', file.name);
        console.log('[V260] File size:', file.size, 'bytes');
        console.log('[V260] File type:', file.type);
        
        // Step 4: Upload to Cloudinary using signed upload
        console.log('[V260] Step 4: Uploading to Cloudinary...');
        console.log('[V260] Importing uploadToCloudinary...');
        
        const { uploadToCloudinary } = await import('@/lib/cloudinary');
        
        console.log('[V260] Calling uploadToCloudinary with:');
        console.log('[V260] - file:', file.name, file.size, 'bytes');
        console.log('[V260] - userId:', userId);
        console.log('[V260] - userName:', fullName);
        
        const cloudinaryUrl = await uploadToCloudinary(
          file,
          userId,
          fullName || undefined
        );
        
        console.log('[V260] ✅ Step 4 complete - Cloudinary upload finished!');
        console.log('[V260] Cloudinary URL:', cloudinaryUrl);
        
        // Verify it's a Cloudinary URL
        if (cloudinaryUrl?.includes('cloudinary.com')) {
          finalPhotoUrl = cloudinaryUrl;
          // Extract public_id from URL (format: .../blissnet/profile-photos/userId.webp)
          const match = cloudinaryUrl.match(/\/blissnet\/profile-photos\/([^/.]+)/);
          publicId = match ? `blissnet/profile-photos/${match[1]}` : null;
          
          console.log('═══════════════════════════════════════════════════════');
          console.log('[V260] 🎉 SUCCESS! Using Cloudinary URL');
          console.log('[V260] Final photo URL:', finalPhotoUrl);
          console.log('[V260] Public ID:', publicId);
          console.log('═══════════════════════════════════════════════════════');
        } else {
          console.error('[V260] ❌ Upload returned non-Cloudinary URL!');
          console.error('[V260] Returned URL:', cloudinaryUrl);
          throw new Error('Invalid Cloudinary response - not a cloudinary.com URL');
        }
        
      } catch (error: any) {
        console.error('═══════════════════════════════════════════════════════');
        console.error('[V260] ❌ UPLOAD FAILED');
        console.error('═══════════════════════════════════════════════════════');
        console.error('[V260] Error type:', error?.constructor?.name);
        console.error('[V260] Error message:', error?.message);
        console.error('[V260] Error stack:', error?.stack);
        console.error('[V260] Full error:', error);
        console.error('[V260] Falling back to Google URL');
        console.error('═══════════════════════════════════════════════════════');
        
        // Fallback to Google URL
        finalPhotoUrl = avatarUrl;
        publicId = null;
      }
    }
    
    // V263.9: CRITICAL FIX - Only INSERT new users, NEVER overwrite existing users!
    // Previous bug: upsert with ignoreDuplicates: false was overwriting user data on every login
    console.log('[V263.9 Profile] Step 1: Checking if user already exists...');
    
    // V263.9: First check if user exists (lightweight query)
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id, name, photo_urls')
      .eq('id', userId)
      .maybeSingle();
    
    if (checkError) {
      console.error('[V263.9 Profile] ❌ Error checking user existence:', checkError);
      // Continue anyway - will try to insert/upsert
    }
    
    if (existingUser) {
      // V263.9: USER EXISTS - DO NOT OVERWRITE!
      console.log('═══════════════════════════════════════════════════════');
      console.log('[V263.9 Profile] ✅ EXISTING USER DETECTED - PRESERVING DATA');
      console.log('═══════════════════════════════════════════════════════');
      console.log('[V263.9 Profile] User ID:', existingUser.id);
      console.log('[V263.9 Profile] Existing name:', existingUser.name);
      console.log('[V263.9 Profile] Existing photos:', existingUser.photo_urls?.length || 0);
      console.log('[V263.9 Profile] SKIPPING user table update to prevent data loss!');
      console.log('═══════════════════════════════════════════════════════');
    } else {
      // V272.6: NEW USER - Create profile via edge function (bypasses RLS)
      console.log('═══════════════════════════════════════════════════════');
      console.log('[V272.6 Profile] 🆕 NEW USER - Creating profile via edge function');
      console.log('═══════════════════════════════════════════════════════');

      const userData: Record<string, any> = {
        name: fullName,
        photo_urls: finalPhotoUrl ? [finalPhotoUrl] : []
      };

      // V259: Add profile_image_public_id if we uploaded to Cloudinary
      if (publicId) {
        userData.profile_image_public_id = publicId;
      }

      console.log('[V272.6] Calling edge function with:', { userId, userEmail: user.email, userData });

      // V272.6: Call edge function instead of direct upsert (bypasses RLS)
      const { data, error } = await supabase.functions.invoke('ensure-user-row', {
        body: {
          userId: userId,
          userEmail: user.email,
          userData: userData
        }
      });

      if (error) {
        console.error('[V272.6] ❌ ensure-user-row failed:', error);
        return false;
      }

      if (!data?.user) {
        console.error('[V272.6] ❌ Edge function succeeded but returned no user');
        return false;
      }

      console.log('[V272.6] ✅ User created via edge function:', data.user.id);
      console.log('[V272.6] User data result:', JSON.stringify(data.user, null, 2));
    }
    
    // =========================================================================
    // V314.6: PROFILES TABLE - Now handled by DB trigger (no app-side creation)
    // =========================================================================
    // Profile row is automatically created by database trigger on public.users insert
    // See migration: 20260130_v3146_profiles_trigger.sql
    // This eliminates race conditions and 409 duplicate key errors during signup
    console.log('[V314.6 Profile] ✅ Profile creation handled by DB trigger (no app action needed)');

    console.log('[V314.6 Profile] ========================================');
    console.log('[V314.6 Profile] 🎉 AUTH FLOW COMPLETE - USER DATA PRESERVED');
    console.log('[V314.6 Profile] ========================================');
    
    return true;
    
  } catch (err: any) {
    console.error('[V212 Profile] ========================================');
    console.error('[V212 Profile] ❌ UNEXPECTED ERROR');
    console.error('[V212 Profile] Error:', err);
    console.error('[V212 Profile] Error message:', err instanceof Error ? err.message : 'Unknown');
    console.error('[V212 Profile] Error stack:', err instanceof Error ? err.stack : 'No stack');
    console.error('[V212 Profile] ========================================');
    return false;
  }
}
